import { HttpBackend, HttpClient } from '@angular/common/http';
import { lastValueFrom, timeout } from 'rxjs';

export interface RuntimeAuthConfig {
    authDomain?: string | null;
    newUniversalLogin?: boolean | null;
}

// The helper only overlays the auth fields, so it accepts anything carrying `authconfig`
// (the strict Environment object isn't structurally assignable without a cast at call sites).
interface RuntimeAuthTarget {
    domain: string;
    authconfig: { domain: string; newUniversalLogin?: boolean };
}

// Auth hosts each environment may switch its login to. Keyed per-env (not one global list) so a
// tampered auth-runtime.json on one env can't point login at another tenant. The override may
// only switch among these plus the compiled default — never an arbitrary host, since login.service
// navigates to https://<domain>/continue?state=...&nfToken=... . Adding a domain is a deliberate,
// reviewed change here. Unknown/dev envs get an empty set → only the compiled domain is accepted.
export const ALLOWED_AUTH_DOMAINS_BY_ENV: Record<string, readonly string[]> = {
    sandbox: ['netfoundry-sandbox.auth0.com', 'login.sandbox-nfconsole.io', 'login.sandbox.netfoundry.io'],
    staging: ['netfoundry-staging.auth0.com', 'login.staging.netfoundry.io'],
    production: ['netfoundry-production.auth0.com', 'login.netfoundry.io'],
};

// Per-deploy runtime override for the Auth0 domain + New Universal Login flag, read at
// app-init from a static asset so Classic <-> ACUL can be switched without an app rebuild.
// Fail-safe: a missing/inert file, parse error, or timeout leaves the compiled env intact.
export async function applyRuntimeAuthConfig(environment: RuntimeAuthTarget, handler: HttpBackend): Promise<void> {
    try {
        const http = new HttpClient(handler);
        const url = `/assets/config/auth-runtime.json?t=${Date.now()}`;
        const cfg = await lastValueFrom(http.get<RuntimeAuthConfig>(url).pipe(timeout(3000)));
        // Keep the domain + ACUL flag coherent: if a requested authDomain is rejected, do NOT
        // flip newUniversalLogin (which would run ACUL against the classic compiled domain).
        let domainOk = true;
        if (cfg?.authDomain) {
            const requested = cfg.authDomain.trim().toLowerCase();
            const allowed = new Set([
                environment.authconfig.domain.toLowerCase(),
                ...(ALLOWED_AUTH_DOMAINS_BY_ENV[environment.domain] ?? []),
            ]);
            if (allowed.has(requested)) {
                environment.authconfig.domain = requested;
            } else {
                domainOk = false;
                // eslint-disable-next-line no-console
                console.warn(`[auth-runtime] ignoring authDomain "${cfg.authDomain}" — not allowed for env "${environment.domain}"`);
            }
        }
        if (domainOk && typeof cfg?.newUniversalLogin === 'boolean') {
            environment.authconfig.newUniversalLogin = cfg.newUniversalLogin;
        }
    } catch {
        // No runtime override -> compiled defaults (classic on the tenant's auth0.com domain).
    }
}
